import { useState, useMemo, useCallback } from "react";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { SearchX, List as ListIcon, LayoutGrid, FilePlus } from "lucide-react";
import { Button } from "@/components/ui";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { useTranslation } from "@/packages/utils";
import { PageHeader, StateCard } from "@/components/ui";
import type { ViewOption, SortOption, SortState } from "@/components/ui";
import {
  ReportListView,
  ReportGridView,
  CreateReportSheet,
} from "@/components/reports";
import type { ReportView } from "@/components/reports";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useLocalStorage } from "@/hooks/use-local-storage";

const DEFAULT_SORT: SortState = { field: "generatedAt", direction: "desc" };

const STATUS_ORDER = ["generating", "ready", "failed"] as const;

function groupReportsByStatus(
  reports: ReportView[],
): Map<string, ReportView[]> {
  const groups = new Map<string, ReportView[]>();
  for (const status of STATUS_ORDER) {
    groups.set(status, []);
  }
  for (const report of reports) {
    const arr = groups.get(report.status);
    if (arr) arr.push(report);
    else groups.set(report.status, [report]);
  }
  // Remove empty groups
  for (const [key, val] of groups) {
    if (val.length === 0) groups.delete(key);
  }
  return groups;
}

function ReportsPage() {
  const { t } = useTranslation(["reports", "common"]);
  const { workspaceSlug } = useParams({ strict: false });
  const navigate = useNavigate();

  const [activeView, setActiveView] = useLocalStorage(
    "reports-active-view",
    "list",
  );
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue);
  const [sortState, setSortState] = useState<SortState>(DEFAULT_SORT);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const { data: rawReports, isLoading } = useWorkspaceQuery(
    api.reports.list,
    {},
  );

  const viewOptions: ViewOption[] = useMemo(
    () => [
      {
        id: "list",
        label: t("reports:views.list"),
        icon: ListIcon,
        active: activeView === "list",
      },
      {
        id: "grid",
        label: t("reports:views.grid"),
        icon: LayoutGrid,
        active: activeView === "grid",
      },
    ],
    [t, activeView],
  );

  const handleViewChange = useCallback(
    (id: string) => {
      setSelectedIds(new Set());
      setActiveView(id);
    },
    [setActiveView],
  );

  const sortOptions: SortOption[] = useMemo(
    () => [
      {
        id: "generatedAt",
        label: t("reports:list.sort.generatedAt"),
      },
      { id: "auditName", label: t("reports:list.sort.auditName") },
      { id: "status", label: t("reports:list.sort.status") },
    ],
    [t],
  );

  // Filter + sort reports
  const reports: ReportView[] = useMemo(() => {
    if (!rawReports) return [];

    let filtered = rawReports.map(
      (r): ReportView => ({
        id: r.id,
        auditId: r.auditId,
        auditName: r.auditName,
        auditCode: r.auditCode,
        auditStatus: r.auditStatus,
        generatedAt: r.generatedAt,
        status: r.status,
        isDraft: r.isDraft,
        version: r.version,
        pdfUrl: r.pdfUrl,
      }),
    );

    // Search filter
    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.auditName.toLowerCase().includes(search) ||
          r.auditCode?.toLowerCase().includes(search),
      );
    }

    // Sort
    filtered.sort((a, b) => {
      const dir = sortState.direction === "asc" ? 1 : -1;
      switch (sortState.field) {
        case "auditName":
          return dir * a.auditName.localeCompare(b.auditName);
        case "status":
          return dir * a.status.localeCompare(b.status);
        default:
          return dir * (a.generatedAt - b.generatedAt);
      }
    });

    return filtered;
  }, [rawReports, debouncedSearch, sortState]);

  const groupedReports = useMemo(
    () => groupReportsByStatus(reports),
    [reports],
  );

  const handleSelectionChange = useCallback((nextIds: Set<string>) => {
    setSelectedIds(new Set(nextIds));
  }, []);

  const handleRowClick = useCallback(
    (reportId: string) => {
      const report = reports.find((r) => r.id === reportId);
      if (!report) return;
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: { workspaceSlug: workspaceSlug ?? "", auditId: report.auditId },
        search: { tab: "overview", openReport: true },
      });
    },
    [reports, navigate, workspaceSlug],
  );

  const handleDownload = useCallback((report: ReportView) => {
    if (!report.pdfUrl) return;
    window.open(report.pdfUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handleViewAudit = useCallback(
    (auditId: string) => {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: { workspaceSlug: workspaceSlug ?? "", auditId },
      });
    },
    [navigate, workspaceSlug],
  );

  const handleBulkDownload = useCallback(() => {
    for (const id of selectedIds) {
      const report = reports.find((r) => r.id === id);
      if (report) handleDownload(report);
    }
  }, [selectedIds, reports, handleDownload]);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    setSelectedIds(new Set());
  }, []);

  const isEmpty = !isLoading && reports.length === 0;
  const hasSearch = searchValue.length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 lg:p-6">
      <PageHeader
        title={t("reports:list.title")}
        viewOptions={viewOptions}
        onViewChange={handleViewChange}
        actionSlot={
          <Button
            variant="primary"
            icon={FilePlus}
            onClick={() => setShowCreateSheet(true)}
          >
            {t("reports:create.title")}
          </Button>
        }
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t("reports:list.searchPlaceholder")}
        sortOptions={sortOptions}
        activeSort={sortState}
        onSortChange={setSortState}
        selectedCount={selectedIds.size}
        onBulkDownload={selectedIds.size > 0 ? handleBulkDownload : undefined}
      />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="loading">
              <StateCard.Icon />
              <StateCard.Title>{t("reports:list.loading")}</StateCard.Title>
              <StateCard.Description>
                {t("reports:list.loadingDescription")}
              </StateCard.Description>
            </StateCard>
          </div>
        )}

        {/* Empty — no match */}
        {isEmpty && hasSearch && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("reports:list.noMatch")}</StateCard.Title>
              <StateCard.Description>
                {t("reports:list.noMatchDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
                >
                  <SearchX size={14} />
                  {t("reports:list.clearFilters")}
                </button>
              </StateCard.Action>
            </StateCard>
          </div>
        )}

        {/* Empty — no data */}
        {isEmpty && !hasSearch && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("reports:list.empty")}</StateCard.Title>
              <StateCard.Description>
                {t("reports:list.emptyDescription")}
              </StateCard.Description>
            </StateCard>
          </div>
        )}

        {/* Report views */}
        {!isLoading && !isEmpty && (
          <AnimatePresence mode="wait">
            {activeView === "list" && (
              <ReportListView
                key="list"
                groupedReports={groupedReports}
                onRowClick={handleRowClick}
                onDownload={handleDownload}
                onViewAudit={handleViewAudit}
                onSelectionChange={handleSelectionChange}
                selectedIds={selectedIds}
              />
            )}

            {activeView === "grid" && (
              <ReportGridView
                key="grid"
                reports={reports}
                onCardClick={handleRowClick}
                onDownload={handleDownload}
              />
            )}
          </AnimatePresence>
        )}
      </div>

      <CreateReportSheet
        open={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
      />
    </div>
  );
}

export const Route = createFileRoute("/$workspaceSlug/reports/")({
  component: ReportsPage,
});
